import type { Lesson, LessonTitleStyle } from '@/types'

export const LESSON_META_COLOR = '#5C5854'

type LessonStructuredTitleSource = Pick<
  Lesson,
  'title' | 'short_title' | 'week_number' | 'main_work_title'
>

type LessonRowTitleSource = LessonStructuredTitleSource & {
  published_at?: string | null
}

export type LessonRowTitleParts = {
  displayTitle: string
  metaLine: string | null
  metaPrefix: string | null
  metaSuffix: string | null
}

type LessonStructuredTitleOptions = {
  weekFormat?: 'short' | 'long'
}

export function getLessonDisplayTitle(lesson: LessonStructuredTitleSource): string {
  const shortTitle = lesson.short_title?.trim()
  return shortTitle && shortTitle.length > 0 ? shortTitle : lesson.title
}

export function getLessonStructuredTitleMeta(
  lesson: LessonStructuredTitleSource,
  { weekFormat = 'long' }: LessonStructuredTitleOptions = {},
) {
  const mainWorkTitle = lesson.main_work_title?.trim()
  const weekLabel =
    lesson.week_number != null
      ? weekFormat === 'short'
        ? `W${lesson.week_number}`
        : `Week ${lesson.week_number}`
      : null
  const metaLine =
    mainWorkTitle && mainWorkTitle.length > 0
      ? `${mainWorkTitle}${weekLabel ? ` - ${weekLabel}` : ''}`
      : null

  return {
    displayTitle: getLessonDisplayTitle(lesson),
    mainWorkTitle,
    metaLine,
    weekLabel,
  }
}

export function formatLessonPublishedDate(
  publishedAt: string | null | undefined,
): string | null {
  if (!publishedAt) return null
  const date = new Date(publishedAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function getLessonRowTitleParts(
  lesson: LessonRowTitleSource,
  options: {
    lessonTitleStyle?: LessonTitleStyle | null
    lessonNumber: number
    weekFormat?: 'short' | 'long'
  },
): LessonRowTitleParts {
  const { lessonTitleStyle, lessonNumber, weekFormat = 'long' } = options
  const structured = getLessonStructuredTitleMeta(lesson, { weekFormat })
  const style = lessonTitleStyle ?? 'default'

  if (style === 'song_and_week') {
    return {
      displayTitle: structured.displayTitle,
      metaLine: structured.metaLine,
      metaPrefix: structured.mainWorkTitle ?? null,
      metaSuffix: structured.weekLabel,
    }
  }

  if (style === 'date') {
    return {
      displayTitle: structured.displayTitle,
      metaLine: formatLessonPublishedDate(lesson.published_at),
      metaPrefix: null,
      metaSuffix: null,
    }
  }

  return {
    displayTitle: lesson.title,
    metaLine: `Lesson ${lessonNumber}`,
    metaPrefix: null,
    metaSuffix: null,
  }
}
